import React from 'react';
import { noop } from 'utils/noop';
import { CustomPicker } from 'react-color';
import { Hue, Saturation } from 'react-color/lib/components/common';
import colorUtil from 'react-color/lib/helpers/color';
import { Popover, PopoverTrigger } from 'ui/popover';
import ColorPreset from './ColorPreset';
import {
  PickerContainer,
  SaturationContainer,
  HueContainer,
  StyledCoreInput,
  PresetContainer,
  StyledPopoverContent,
  ColorBox,
} from './ColorPicker.styled';

// type Props = {
//   /**
//    * Callback function when color value is changed.
//    * ```
//    * onChangeColor(colorDef)
//    * ```
//    * @param { Object } colorProps: Color object of updated color.
//    * @param { String } value: Updated color hex value.
//    * @returns { void }
//    */
//   onChange: (colorDef: {}, value: string) => {},
//   /** An array of preset colors to show. */
//   presets: [string],
//   /** Value of the color in hex. */
//   value: string,
// };

export class ColorPicker extends React.Component {
  static defaultProps = {
    onChange: noop,
    presets: [
      '#eb144c',
      '#ff6900',
      '#fcb900',
      '#00d034',
      '#0693e3',
      '#2628f0',
      '#9900ef',
      '#f78da7',
      '#b57133',
      '#abb8c3',
    ],
    value: '#ffffff',
  };

  constructor(props) {
    super(props);
    this.state = {
      color: colorUtil.toState(props.value),
      value: props.value,
    };
  }

  shouldComponentUpdate = (nextProps) => {
    const { value: nextValue } = nextProps;
    const { value } = this.props;
    if (value !== nextValue) {
      this.handleChange({ hex: nextValue });
    }
    return true;
  };

  /**
   * handleBlurHex
   * Callback function when hex text box is blurred.
   * @returns { void }
   */
  handleBlurHex = () => {
    this.setState({ value: this.state.color.hex });
  };

  /**
   * handleChange
   * Callback function when color is changed.
   * @param { Object } colorDef: Color object of new color.
   * @returns { Object } New color object of selected color.
   */
  handleChange = (colorDef) => {
    const { onChange, ...otherProps } = this.props;
    const { color: stateColor } = this.state;
    const color = colorUtil.toState(colorDef, stateColor.oldHue);
    this.setState({ color, value: color.hex });
    onChange({}, { ...otherProps, value: color.hex });
    return color;
  };

  /**
   * handleChangeHex
   * Callback function when value of hex text box is changed.
   * @param { Object } e: The change event.
   * @param { Object } textBoxProps: Updated props of textbox containing new value.
   * @returns { void }
   */
  handleChangeHex = (e, { value }) => {
    if (colorUtil.isValidHex(value)) {
      const { onChange, ...otherProps } = this.props;
      const color = this.handleChange({ hex: value });
      onChange(e, { ...otherProps, value: color.hex });
    }
    this.setState({ value });
  };

  /**
   * handleClickButton
   * Callback function color button is clicked.
   * @returns { void }
   */
  handleClickButton = () => {
    const { openClick } = this.state;
    if (openClick) {
      this.setState({ open: false, openClick: false });
    } else {
      this.setState({ open: true, openClick: true });
    }
  };

  /**
   * handleClickPreset
   * Callback function when a preset is clicked.
   * @param { Object } e: The click event.
   * @param { Object } presetProps: The props of the preset.
   * @returns { void }
   */
  handleClickPreset = (e, { color }) => {
    this.handleChange({ hex: color });
  };

  /**
   * generatePresets
   * Creates the color preset boxes.
   * @returns { Array } Array of preset boxes.
   */
  generatePresets = () => {
    const { presets } = this.props;
    const { color } = this.state;
    return presets.map((preset, index) => (
      <ColorPreset
        key={`preset-${index}`}
        color={preset}
        onClick={this.handleClickPreset}
        selected={color.hex.toLowerCase() === preset.toLowerCase()}
      />
    ));
  };

  render() {
    const { color, value } = this.state;

    return (
      <Popover>
        <StyledPopoverContent>
          <PickerContainer>
            <SaturationContainer>
              <Saturation {...color} onChange={this.handleChange} />
            </SaturationContainer>
            <HueContainer>
              <Hue {...color} onChange={this.handleChange} />
            </HueContainer>
            <StyledCoreInput
              label={{ basic: true, content: 'hex' }}
              labelPosition="left"
              onBlur={this.handleBlurHex}
              onChange={this.handleChangeHex}
              value={value}
            />
            {this.props.presets && <PresetContainer>{this.generatePresets()}</PresetContainer>}
          </PickerContainer>
        </StyledPopoverContent>
        <PopoverTrigger>
          <ColorBox style={{ background: color.hex }} onClick={this.handleClickButton} />
        </PopoverTrigger>
      </Popover>
    );
  }
}

export default CustomPicker(ColorPicker);
